import { Box, Typography, Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { fetchPresignUrl } from '../utils/apiHelper';
import PlotlyFileRenderer from './PlotlyFileRenderer';
import ResultsTable from './ResultsTable';

function ResultPage({ mode, user }) {
    const [selectedJob, setSelectedJob] = useState(null);
    const [fileUrls, setFileUrls] = useState({});

    // Function to generate file paths based on job details
    const generateFilePaths = (job) => {
        if (!job || !job.JobOutput) return [];

        // Split JobOutput into individual filenames
        const filenames = job.JobOutput.split(',');

        // Generate full paths using jobtype and jobid
        return filenames.map(filename => ({
            filename,
            path: `${job.JobType}/${job.JobID}/${filename.trim()}`
        }));
    };

    const fetchFileUrls = useCallback(async (job) => {
        if (!job || !job.JobOutput) return;

        const paths = generateFilePaths(job);
        const urlPromises = paths.map(async (file) => {
            try {
                const url = await fetchPresignUrl(user, file.path, 'download');
                return { [file.filename]: url };
            } catch (error) {
                console.error(`Error fetching URL for ${file.filename}`, error);
                return { [file.filename]: null };
            }
        });

        const urlResults = await Promise.all(urlPromises);
        const urlMap = urlResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
        setFileUrls(urlMap);
    }, [user]);

    useEffect(() => {
        if (selectedJob) {
            fetchFileUrls(selectedJob);
        }
    }, [selectedJob, fetchFileUrls]);

    // Render file previews with zoom
    const renderFilePreview = (filename) => {
        const url = fileUrls[filename];
        if (!url) return null;

        if (filename.endsWith('.png')) {
            return (
                <Zoom>
                    <img
                        src={url}
                        alt={filename}
                        style={{
                            // maxWidth: '300px', 
                            maxHeight: '300px',
                            margin: '10px 0'
                        }}
                    />
                </Zoom>
            );
        }

        if (filename.endsWith('.plotly.json')) {
            return (<PlotlyFileRenderer url={url} />);
        }

        if (filename.endsWith('.csv')) {
            return (
                <a
                    href={url}
                    download={filename}
                    style={{ display: 'block', margin: '10px 0' }}
                >
                    Download {filename}
                </a>
            );
        }

        if (filename.endsWith('.html')) {
            return (
                <Button
                    variant="outlined"
                    onClick={() => window.open(url, '_blank')}
                    sx={{
                        margin: '10px 0',
                        width: '100%'
                    }}
                >
                    Open {filename} in New Tab
                </Button>
            );
        }

        return null;
    };

    return (
        <Box sx={{
            display: 'flex',
            width: '100%',
            gap: '0em',             // Add gap between columns
            flexWrap: {
                xs: 'wrap',           // Allow wrapping on smaller screens
                md: 'nowrap'          // Keep inline on medium and larger screens
            }
        }}>
            <Box
                sx={{
                    width: {
                        xs: '100%',         // Full width on small screens
                        md: '40%'           // 40% width on medium and larger screens
                    },
                    marginTop: '2em',
                    padding: '1em',
                    border: '1px solid #ccc',
                    borderRadius: '15px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    backgroundColor: mode === 'light' ? '#f9f9f9' : '#424242',
                    color: mode === 'light' ? '#000' : '#fff',
                    marginRight: '1em'
                }}
            >
                <Typography variant="h2" sx={{ fontSize: '1.5rem' }}>
                    Results
                </Typography>
                <ResultsTable
                    mode={mode}
                    onRowSelect={setSelectedJob}
                />
            </Box>
            <Box sx={{
                width: {
                    xs: '100%',         // Full width on small screens
                    md: '60%'            // 60% width on medium and larger screens
                },
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Box
                    sx={{
                        marginTop: '2em',
                        padding: '1em',
                        border: '1px solid #ccc',
                        borderRadius: '15px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        backgroundColor: mode === 'light' ? '#f9f9f9' : '#424242',
                        color: mode === 'light' ? '#000' : '#fff',
                        marginBottom: '1em'
                    }}
                >
                    <Typography variant="h2" sx={{ fontSize: '1.5rem' }}>
                        Preview - {selectedJob ? (selectedJob.JobLabel || '') : 'No Job Selected'}
                    </Typography>
                    {selectedJob && (
                        <Box>
                            {generateFilePaths(selectedJob).map((file, index) => (
                                <Box key={index}>
                                    <Typography variant="subtitle1">
                                        {file.filename}
                                    </Typography>
                                    {renderFilePreview(file.filename)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                <Box sx={{
                    marginTop: '2em',
                    padding: '1em',
                    border: '1px solid #ccc',
                    borderRadius: '15px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    backgroundColor: mode === 'light' ? '#f9f9f9' : '#424242',
                    color: mode === 'light' ? '#000' : '#fff'
                }}>
                    <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1em' }}>
                        Job Details
                    </Typography>
                    {selectedJob ? (
                        <>
                            <p><strong>Job ID:</strong> {selectedJob.JobID}</p>
                            <p><strong>Job Type:</strong> {selectedJob.JobType}</p>
                            <p><strong>Job Label:</strong> {selectedJob.JobLabel || 'N/A'}</p>
                            {selectedJob.JobParams && (
                                <p>
                                    <strong>Job Params:</strong>
                                    <pre style={{
                                        whiteSpace: 'pre-wrap',
                                        wordWrap: 'break-word',
                                        backgroundColor: mode === 'light' ? '#f4f4f4' : '#333',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        margin: '10px 0'
                                    }}>
                                        {JSON.stringify(selectedJob.JobParams, null, 2)}
                                    </pre>
                                </p>
                            )}
                        </>
                    ) : (
                        <p>Select a job to view details</p>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ResultPage;
